import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { gravarCv, gravarIndices, gravarPreAdensamento } from '../api/adensamento';
import { cientifica, paraCampo, paraNumero, rotulo } from '../formato';
import { useSessao } from '../sessao';
import { CabecalhoDaFicha, useFicha } from '../Ficha';
import { Aviso, Campo, Carregando, Escolha, ErroDaApi, Selo } from '../ui';
import type { Opcao } from '../ui';
import {
  Laudo, Pendencias, n, num, useAdensamento,
} from './Adensamento';
import type { BlocoProps, Numerico } from './Adensamento';
import {
  CasagrandeNoGrafico, CurvaNoGrafico, Dica, TRECHOS, TaylorNoGrafico,
  type CasagrandeTracado, type Selecao, type TaylorTracado, type Trecho,
} from './AdensamentoGraficos';
import { Plano, Serie, useJanela, type Ponto } from '../Grafico';

/**
 * Interpretar um adensamento — a MESA.
 *
 * ─── Por que esta tela existe ──────────────────────────────────────────
 *
 * Porque a de lançamento não devia fazer isto. Humberto, em 23/08/2026:
 * «As interpretações de tensão de pré-adensamento, Cv e coeficientes da
 * reta não ocorrem na tela do laboratorista. Esses cálculos e
 * interpretações ocorrem na elaboração do relatório. Atualmente está
 * misturado.»
 *
 * A separação não é estética. São três coisas distintas:
 *
 *  1. **Ofício diferente.** Lá se pesa e se cola arquivo. Aqui se traça
 *     Taylor ou Casagrande a régua sobre a curva impressa, se escolhe
 *     quais dois estágios formam a reta virgem, se decide onde está a
 *     σ'p. São leituras de engenharia, não leituras de instrumento.
 *
 *  2. **Momento diferente.** O ensaio termina no desmonte; a
 *     interpretação começa quando alguém senta com as curvas — dias
 *     depois, às vezes semanas, junto da redação do relatório.
 *
 *  3. **Permissão diferente.** `ensaio.processar`, e não
 *     `ensaio.lancar`. A API já separava as duas desde a `0024`; era a
 *     tela que oferecia os campos a quem não devia ter acesso a eles. Um
 *     formulário que aparece para quem não pode gravar não protege nada
 *     e ensina a tentar.
 *
 * ─── O que esta tela NÃO faz ───────────────────────────────────────────
 *
 * **Não traça sozinha.** Não há botão «ajustar reta automaticamente». O
 * sistema guarda o que a pessoa traçou, com o nome dela, e refaz a
 * aritmética da norma a partir dali (`D-27`). Um sistema que ajusta a
 * reta e não diz que ajustou está assinando no lugar de alguém.
 *
 * **Não deixa lançar dado de bancada.** Massa, cápsula e leitura ficam
 * na outra tela, e o link no topo leva até lá. Segregação de funções
 * (briefing §15) vale nos dois sentidos: quem interpreta também não
 * reescreve o que foi pesado.
 *
 * **Não esconde os campos de quem só pode ver.** Ela os DESABILITA, com
 * a razão escrita. Esconder faria a pessoa pensar que a etapa não
 * existe; desabilitar mostra o que falta e de quem é a vez.
 */

export function AdensamentoInterpretacao() {
  const { id = '' } = useParams();
  const { pode } = useSessao();
  const { d, erro, ok, salvando, gravar, carregar, campoErro }
    = useAdensamento(id);

  /* RN-238 (onda 290): interpretar é assinatura própria. */
  const podeProcessar = pode('ensaio.interpretar');

  /*
   * A σ′p que a construção de Pacheco Silva sugere sobe do bloco 2 para o
   * 3. É o único número que atravessa dois blocos, e atravessa porque a
   * construção é feita na curva (bloco 2) e assinada no formulário
   * (bloco 3). Duplicar o desenho no bloco 3 seria desenhar a mesma
   * construção duas vezes e deixar as duas divergirem.
   */
  const [sigmaSugerida, setSigmaSugerida] = useState<number | null>(null);

  /*
   * A seleção dos trechos mora AQUI, e não dentro do bloco 2.
   *
   * O bloco 3 desenha a construção de Pacheco Silva sobre o mesmo trecho
   * virgem que o bloco 2 escolheu. Guardar a escolha em cada bloco daria
   * dois trechos virgens possíveis para o mesmo ensaio — e um laudo cujo
   * gráfico de índices discorda do de pré-adensamento.
   */
  const [selecao, setSelecao] = useState<Selecao>({});
  const ficha = useFicha(id, 'NBR 16853', ok);

  if (erro && !d) return <ErroDaApi erro={erro} />;
  if (!d) return <Carregando texto="Abrindo o ensaio" />;

  /*
   * No Carbon (onda 239): o mesmo cabeçalho de toda ficha — sigla, norma
   * vigente, estado e identificação —, com o caminho de volta para o
   * lançamento logo abaixo. A mesa de interpretação continua sendo a
   * mesma: os gráficos e o que se traça neles não mudaram.
   */
  /*
   * Sem `ensaio.interpretar`, a interpretação não se vê nem se edita
   * (RN-238, onda 290): «não pode ser vista e editada, apenas pelos
   * usuários que tiverem a permissão» — Humberto, 06/10/2026. Até a 290
   * quem não traçava via os campos fechados.
   */
  if (!podeProcessar) {
    return (
      <div className="tela adensamento ficha-carbon interpretacao">
        <CabecalhoDaFicha id={id} sigla={ficha?.metodo_codigo ?? 'ADNS'}
          titulo="Adensamento — interpretação" ficha={ficha}
          normaDaTela="ABNT NBR 16853" />
        <p className="sub fe-volta">
          <Link to={`/ensaio/${id}/adensamento?etapa=4`}>← voltar ao lançamento</Link>
        </p>
        <Aviso tipo="info" titulo="A interpretação é de quem tem a permissão">
          Traçar e consultar cv, os índices de compressão e a tensão de
          pré-adensamento é de quem tem a permissão «Interpretar resultados»
          (<code>ensaio.interpretar</code>), concedida em Usuários e acessos.
        </Aviso>
      </div>
    );
  }

  return (
    <div className="tela adensamento ficha-carbon interpretacao">
      <CabecalhoDaFicha id={id} sigla={ficha?.metodo_codigo ?? 'ADNS'}
        titulo="Adensamento — interpretação" ficha={ficha}
        normaDaTela="ABNT NBR 16853" />
      <p className="sub fe-volta">
        <Link to={`/ensaio/${id}/adensamento?etapa=4`}>← voltar ao lançamento</Link>
      </p>


      <Pendencias itens={d.pendencias} id={id}
        detalhe={d.pendenciasDetalhadas} aoJustificar={carregar} />

      {erro ? <ErroDaApi erro={erro} /> : null}
      {ok && !erro && <Aviso tipo="ok">{ok}</Aviso>}

      <Curva d={d} />

      <Cv d={d} id={id} gravar={gravar} salvando={salvando}
        pode={podeProcessar} />

      <Indices d={d} id={id} gravar={gravar} salvando={salvando}
        pode={podeProcessar}
        aoSugerirSigma={setSigmaSugerida}
        sel={selecao} setSel={setSelecao} />

      <PreAdensamento d={d} id={id} gravar={gravar} salvando={salvando}
        pode={podeProcessar}
        campoErro={campoErro} sugerida={sigmaSugerida}
        selecao={selecao} aoSugerirSigma={setSigmaSugerida} />

      <Laudo id={id} pendencias={d.pendencias} numero={4} />
    </div>
  );
}

// ── 0 · a curva, em números ──────────────────────────────────────────

/**
 * A tabela e × log σ, que é sobre o que se traça tudo o mais.
 *
 * Ela aparece PRIMEIRO e é só leitura: quem interpreta precisa da curva
 * à vista enquanto preenche, e não pode editá-la aqui. Os números vêm
 * das views do banco, os mesmos que o laudo imprime — se divergissem,
 * a divergência só apareceria no documento assinado (`D-29`).
 */
function Curva({ d }: { d: any }) {
  return (
    <section className="bloco">
      <h2>Curva de compressibilidade</h2>
      <p className="sub">
        Altura e índice de vazios ao final de cada estágio (7.2.2). É a
        curva impressa sobre a qual se traçam Taylor, Casagrande e a σ'p.
      </p>
      <div className="rola">
        <table className="dados">
          <thead>
            <tr>
              <th>#</th><th className="num">σ (kPa)</th><th>Fase</th>
              <th className="num">Altura (mm)</th>
              <th className="num">e</th>
              <th className="num">Leituras</th>
              <th>cv</th>
            </tr>
          </thead>
          <tbody>
            {d.estagios.map((s: any) => (
              <tr key={s.id}>
                <td>{s.ordem}</td>
                <td className="num mono">{num(s.pressao_kpa, 1)}</td>
                <td>
                  <Selo tom={s.fase === 'descarregamento' ? 'atencao' : 'neutro'}>
                    {rotulo(s.fase)}
                  </Selo>
                </td>
                <td className="num mono">
                  {s.altura_cm != null ? num(Number(s.altura_cm) * 10, 4) : '—'}
                </td>
                <td className="num mono">{num(s.indice_vazios, 4)}</td>
                <td className="num">{s.leituras}</td>
                <td>
                  {s.cv_metodo
                    ? <Selo tom="ok">{rotulo(s.cv_metodo)}</Selo>
                    : <span className="nota">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!d.estagios.length && (
        <p className="nada">
          Nenhum estágio lançado ainda. A interpretação começa quando a
          bancada terminar.
        </p>
      )}
    </section>
  );
}

// ── 1 · cv ───────────────────────────────────────────────────────────

/**
 * A construção gráfica de Taylor E de Casagrande, lado a lado.
 *
 * «O interessante é calcular o gráfico Cv de Taylor e Casagrande lado a
 *  lado para comparar o resultado em tempo real» — Humberto, 14/09/2026.
 * Os dois métodos leem a mesma curva; ver os dois cv andando enquanto se
 * arrasta é o que diz se o traçado está coerente. Cada painel tem os
 * seus campos, a sua prévia e o seu «Gravar»; só um dos dois sai no
 * laudo (0183), e a escolha fica no painel.
 *
 * O sistema propõe o traçado (187) mas NÃO decide: o que se grava é onde
 * as alças pararam, com o nome de quem gravou.
 */
function Cv({
  d, id, gravar, salvando, pode,
}: BlocoProps & { pode: boolean }) {
  const [estagio, setEstagio] = useState<string | null>(null);
  const s = d.estagios.find((e) => e.id === estagio);
  /* A prévia de cada painel sobe para cá, para o seletor de estágios e o
     conjunto mostrarem o cv do método que vai para o laudo. */
  const [previas, setPrevias] = useState<{ taylor: number | null; casagrande: number | null }>(
    { taylor: null, casagrande: null });

  const cvDe = (m: string) => (s?.cvs ?? []).find((c: any) => c.metodo === m) ?? null;
  const metodoDoLaudo = (s?.cvs ?? []).find((c: any) => c.no_laudo)?.metodo
    ?? s?.cv_metodo ?? 'taylor';
  const previaDoLaudo = previas[metodoDoLaudo as 'taylor' | 'casagrande'];

  const opcoes: Opcao[] = d.estagios
    .filter((e) => e.fase === 'carregamento')
    .map((e) => {
      const g = e.cv_cm2_s != null ? Number(e.cv_cm2_s) : null;
      const aberto = e.id === estagio && previaDoLaudo != null
        && (g == null || Math.abs(g - previaDoLaudo) > Math.abs(g) * 1e-6);
      return {
        valor: e.id,
        texto: `${e.ordem} · ${num(e.pressao_kpa, 1)}`,
        /* O estágio aberto mostra o que as alças dizem AGORA, com «?» de
           não gravado; os outros, o do banco (Humberto, 01/09/2026). */
        detalhe: aberto ? `${cientifica(previaDoLaudo as number)} ?`
          : (g != null ? cientifica(g) : '—'),
      };
    });

  return (
    <>
    <section className="bloco">
      <h2>
        <span>1 · Coeficiente de adensamento</span>
        <Dica rotulo="o bloco do coeficiente de adensamento">
          Taylor (√t) e Casagrande (log t) sobre a mesma curva, lado a
          lado: o traçado é seu, o sistema propõe e guarda o que você
          deixou, e refaz a conta a partir dele. Os dois métodos saem no
          laudo; o marcado como <strong>referência</strong> é o que alimenta
          o k<sub>v</sub> e o resumo.
        </Dica>
      </h2>

      <Escolha rotulo="Estágio" opcoes={opcoes} valor={estagio}
        aoEscolher={setEstagio} classe="rolar" />

      {estagio && s && (
        <div className="cv-lado-a-lado">
          <PainelCv metodo="taylor" id={id} estagio={estagio} s={s}
            gravar={gravar} salvando={salvando} pode={pode}
            outro={cvDe('casagrande')}
            aoPrevia={(v) => setPrevias((p) => (p.taylor === v ? p : { ...p, taylor: v }))} />
          <PainelCv metodo="casagrande" id={id} estagio={estagio} s={s}
            gravar={gravar} salvando={salvando} pode={pode}
            outro={cvDe('taylor')}
            aoPrevia={(v) => setPrevias((p) => (p.casagrande === v ? p : { ...p, casagrande: v }))} />
        </div>
      )}

      {estagio && previas.taylor != null && previas.casagrande != null && (
        <p className="sub cv-comparacao">
          Taylor {cientifica(previas.taylor)} · Casagrande {cientifica(previas.casagrande)} —
          razão {num(Math.max(previas.taylor, previas.casagrande)
            / Math.min(previas.taylor, previas.casagrande), 2)}×.
          <Dica rotulo="a razão entre os métodos">
            Os dois métodos costumam ficar na mesma ordem de grandeza; uma
            razão grande é sinal de traçado a rever, não de solo estranho.
          </Dica>
        </p>
      )}
    </section>

    <CvEmConjunto d={d} aberto={estagio} previas={previas} />
    </>
  );
}

/**
 * Um método, um painel: gráfico, campos, prévia, «sai no laudo», Gravar.
 *
 * A prévia é a conta do banco repetida aqui (`coeficiente_adensamento`,
 * 0024) para o gesto ter retorno — o teste 113 compara as duas.
 *   H₅₀ (Taylor)     = H₀ − 5/9 (H₀ − H₉₀)      cv = 0,848 (0,5·H₅₀)²/t₉₀
 *   H₅₀ (Casagrande) = (H₀ + H₁₀₀) / 2         cv = 0,197 (0,5·H₅₀)²/t₅₀
 */
function PainelCv({
  metodo, id, estagio, s, gravar, salvando, pode, outro, aoPrevia,
}: Omit<BlocoProps, 'd'> & {
  metodo: 'taylor' | 'casagrande';
  estagio: string;
  s: any;
  pode: boolean;
  outro: any;
  aoPrevia(v: number | null): void;
}) {
  const [f, setF] = useState<Numerico>({});
  const [origem, setOrigem] = useState<'tela' | 'digitado' | null>(null);
  const [t1t2, setT1t2] = useState<{ t1S?: number; t2S?: number } | null>(null);
  const [noLaudo, setNoLaudo] = useState<boolean | null>(null);
  const gravado = (s?.cvs ?? []).find((c: any) => c.metodo === metodo) ?? null;

  useEffect(() => {
    setF({
      h0Mm: paraCampo(gravado?.h0_mm), h90Mm: paraCampo(gravado?.h90_mm),
      h100Mm: paraCampo(gravado?.h100_mm),
      t90S: paraCampo(gravado?.t90_s), t50S: paraCampo(gravado?.t50_s),
    });
    setOrigem(null); setNoLaudo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estagio, s?.cvs?.length, gravado?.cv_cm2_s]);

  const cvPrevia = (() => {
    const h0 = paraNumero(f.h0Mm ?? '');
    if (h0 == null) return null;
    if (metodo === 'taylor') {
      const h90 = paraNumero(f.h90Mm ?? ''), t90 = paraNumero(f.t90S ?? '');
      if (h90 == null || t90 == null || t90 <= 0) return null;
      return 0.848 * (0.5 * ((h0 - (5 / 9) * (h0 - h90)) / 10)) ** 2 / t90;
    }
    const h100 = paraNumero(f.h100Mm ?? ''), t50 = paraNumero(f.t50S ?? '');
    if (h100 == null || t50 == null || t50 <= 0) return null;
    return 0.197 * (0.5 * (((h0 + h100) / 2) / 10)) ** 2 / t50;
  })();
  useEffect(() => { aoPrevia(cvPrevia); }, [cvPrevia]); // eslint-disable-line react-hooks/exhaustive-deps

  const gravadoCv = gravado?.cv_cm2_s == null ? null : Number(gravado.cv_cm2_s);
  const mudou = cvPrevia != null && gravadoCv != null
    && Math.abs(gravadoCv - cvPrevia) > Math.abs(gravadoCv) * 1e-6;
  const esteSaiNoLaudo = noLaudo ?? (gravado ? !!gravado.no_laudo : !outro);

  const enviar = () => gravar('cv', () => gravarCv(id, estagio, {
    metodo,
    h0Mm: n(f, 'h0Mm') ?? 0,
    h90Mm: metodo === 'taylor' ? n(f, 'h90Mm') : undefined,
    t90S: metodo === 'taylor' ? n(f, 't90S') : undefined,
    h100Mm: metodo === 'casagrande' ? n(f, 'h100Mm') : undefined,
    t50S: metodo === 'casagrande' ? n(f, 't50S') : undefined,
    t1S: metodo === 'casagrande'
      ? (t1t2?.t1S ?? (gravado?.t1_s != null ? Number(gravado.t1_s) : undefined)) : undefined,
    t2S: metodo === 'casagrande'
      ? (t1t2?.t2S ?? (gravado?.t2_s != null ? Number(gravado.t2_s) : undefined)) : undefined,
    origem: origem ?? (gravado?.origem === 'tela' ? 'tela' : undefined),
    noLaudo: noLaudo ?? undefined,
  }));

  const campo = (chave: string, rot: string) => (
    <Campo rotulo={rot} inputMode="decimal" value={f[chave] ?? ''}
      disabled={!pode}
      onChange={(e) => { setOrigem('digitado'); setF({ ...f, [chave]: e.target.value }); }} />
  );
  const origemAtual = origem ?? gravado?.origem;

  return (
    <div className={`painel-cv ${metodo}${esteSaiNoLaudo ? ' no-laudo' : ''}`}>
      <h3>{metodo === 'taylor' ? 'Taylor (√t)' : 'Casagrande (log t)'}</h3>

      {metodo === 'taylor' ? (
        <TaylorNoGrafico
          /* `key` por estágio: trocar de estágio é um gráfico NOVO — a
             janela que a mão ajustou e as alças do anterior não valem para
             a curva seguinte (o painel de Casagrande ficava vazio, com a
             janela do estágio anterior, 14/09/2026). */
          key={estagio}
          ensaioId={id} estagioId={estagio} pode={pode}
          inicial={gravado && gravado.h0_mm != null && gravado.h90_mm != null && gravado.t90_s != null
            ? { h0Mm: Number(gravado.h0_mm), h90Mm: Number(gravado.h90_mm), t90S: Number(gravado.t90_s) }
            : null}
          aoTracar={(v: TaylorTracado | null) => {
            if (!v) return;
            setOrigem('tela');
            setF((antes) => ({ ...antes, h0Mm: paraCampo(v.h0Mm, 4),
              h90Mm: paraCampo(v.h90Mm, 4), t90S: paraCampo(v.t90S, 3) }));
          }}
        />
      ) : (
        <CasagrandeNoGrafico
          key={estagio}
          ensaioId={id} estagioId={estagio} pode={pode}
          inicial={gravado && gravado.h0_mm != null && gravado.h100_mm != null && gravado.t50_s != null
            ? { h0Mm: Number(gravado.h0_mm), h100Mm: Number(gravado.h100_mm),
                t50S: Number(gravado.t50_s), t1S: Number(gravado.t1_s ?? 0),
                t2S: Number(gravado.t2_s ?? 0) }
            : null}
          aoTracar={(v: CasagrandeTracado | null) => {
            if (!v) return;
            setOrigem('tela');
            setT1t2({ t1S: v.t1S, t2S: v.t2S });
            setF((antes) => ({ ...antes, h0Mm: paraCampo(v.h0Mm, 4),
              h100Mm: paraCampo(v.h100Mm, 4), t50S: paraCampo(v.t50S, 3) }));
          }}
        />
      )}

      <div className="form linha">
        {campo('h0Mm', 'H₀ (mm) — 0 % do primário')}
        {metodo === 'taylor' ? (
          <>{campo('h90Mm', 'H₉₀ (mm)')}{campo('t90S', 't₉₀ (s)')}</>
        ) : (
          <>{campo('h100Mm', 'H₁₀₀ (mm)')}{campo('t50S', 't₅₀ (s)')}</>
        )}
      </div>

      <div className={`cv-previa${mudou ? ' mudou' : ''}`}>
        <span className="rotulo">c<sub>v</sub> (cm²/s)</span>
        <strong>{cvPrevia == null ? '—' : cientifica(cvPrevia)}</strong>
        <span className="sub">
          {mudou
            ? `gravado: ${cientifica(gravadoCv as number)} — ainda não é este`
            : (gravadoCv != null ? 'igual ao gravado'
              : 'prévia do traçado — o valor gravado vem do servidor')}
        </span>
        <span className={`sub origem-cv ${origemAtual === 'digitado' ? 'digitado' : ''}`}>
          {origemAtual === 'digitado' ? 'números escritos à mão — o laudo marca com †'
            : origemAtual === 'tela' ? 'traçado na tela, sobre a curva'
              : gravadoCv != null ? 'origem não registrada (traçado anterior)' : ''}
        </span>
        <label className="sub" style={{ display: 'flex', gap: '.4rem', alignItems: 'center' }}>
          <input type="radio" name={`no-laudo-${estagio}`} checked={esteSaiNoLaudo}
            disabled={!pode} onChange={() => setNoLaudo(true)} />
          método de referência{outro ? ' — os dois saem no laudo; este alimenta o kᵥ e o resumo' : ''}
        </label>
      </div>

      <button type="button" disabled={!pode || salvando === 'cv'}
        className={mudou ? 'nao-gravado' : undefined}
        onClick={() => void enviar()}>
        {salvando === 'cv' ? 'Gravando…' : (gravadoCv != null ? 'Regravar' : 'Gravar')}
      </button>
    </div>
  );
}

const METODOS_CV = [
  { chave: 'taylor', rotulo: 'Taylor (√t)' },
  { chave: 'casagrande', rotulo: 'Casagrande (log t)' },
] as const;
type MetodoCv = typeof METODOS_CV[number]['chave'];

/**
 * O marcador de cada método: círculo cheio (Taylor) e losango vazado.
 * `marca-serie`, e não `marcador`: essa classe já é o interruptor com
 * frase da tela de medição.
 */
function Marcador({ metodo }: { metodo: MetodoCv }) {
  return (
    <svg className={`marca-serie ${metodo}`} viewBox="0 0 14 14" aria-hidden="true">
      {metodo === 'taylor'
        ? <circle cx="7" cy="7" r="4.5" />
        : <polygon points="7,2 12,7 7,12 2,7" />}
    </svg>
  );
}

/**
 * O cv de TODOS os estágios, pelos DOIS métodos.
 *
 * «Plote o gráfico tanto de Cv de Taylor quanto Cv de Casagrande, aplique
 *  uma legenda» — Humberto, 14/09/2026. O painel mostrava só o cv do
 * método de referência; com os dois traçados, o que interessa é
 * justamente ver as duas séries caindo juntas (ou não) ao longo das
 * tensões. Mesma convenção do laudo (191): Taylor em círculo cheio e
 * linha contínua, Casagrande em losango vazado e linha tracejada — forma
 * E traço, nunca só a cor (§18).
 *
 * O estágio ABERTO acompanha as alças enquanto se arrasta: a prévia de
 * cada painel entra no lugar do gravado, marcada como provisória. É
 * durante o arrasto que o painel serve — ver onde o cv está caindo em
 * relação aos vizinhos ANTES de gravar.
 */
function CvEmConjunto({ d, aberto, previas }: {
  d: any; aberto: string | null;
  previas: Record<MetodoCv, number | null>;
}) {
  const linhas = (d.estagios as any[])
    .filter((e) => e.fase === 'carregamento')
    .map((e) => {
      /* A tensão média do estágio — 8.4. `pressao_anterior_kpa` já vem
         da visão; a média aqui é a mesma conta, e não uma segunda. */
      const p = Number(e.pressao_kpa);
      const ant = e.pressao_anterior_kpa == null
        ? p : Number(e.pressao_anterior_kpa);
      const ehAberto = e.id === aberto;
      const cvs = (e.cvs ?? []) as any[];
      const por = {} as Record<MetodoCv, {
        cv: number | null; gravado: number | null; provisorio: boolean;
      }>;
      for (const m of METODOS_CV) {
        const g = cvs.find((c) => c.metodo === m.chave)?.cv_cm2_s;
        const gravado = g == null ? null : Number(g);
        const previa = ehAberto ? previas[m.chave] : null;
        por[m.chave] = {
          cv: previa ?? gravado,
          gravado,
          provisorio: previa != null
            && (gravado == null
                || Math.abs(previa - gravado) > Math.abs(gravado) * 1e-6),
        };
      }
      /* O de referência (0184) é o que alimenta o kv e a razão da tabela. */
      const ref = (cvs.find((c) => c.no_laudo)?.metodo
        ?? e.cv_metodo ?? 'taylor') as MetodoCv;
      return { id: e.id, ordem: e.ordem, pressao: p, media: (p + ant) / 2, ehAberto, por, ref };
    })
    .filter((l) => l.media > 0);

  const serieDe = (m: MetodoCv): Ponto[] => linhas
    .filter((l) => l.por[m].cv != null && (l.por[m].cv as number) > 0)
    .map((l) => ({ x: l.media, y: l.por[m].cv as number }));

  const pontos = METODOS_CV.flatMap((m) => serieDe(m.chave));

  /*
   * O ENQUADRAMENTO sai dos estágios GRAVADOS, e não do conjunto.
   *
   * Foi o teste `52.91` que me mostrou por quê: com a janela calculada
   * sobre todos os pontos, arrastar a alça movia o ponto E a janela
   * junto, e o ponto ficava pregado no mesmo pixel. Pior do que o teste
   * falhar é o que isso significa na tela — a pessoa arrasta, o número
   * muda, e o gráfico não se mexe.
   */
  const gravados: Ponto[] = linhas.flatMap((l) => METODOS_CV
    .filter((m) => l.por[m.chave].gravado != null && (l.por[m.chave].gravado as number) > 0)
    .map((m) => ({ x: l.media, y: l.por[m.chave].gravado as number })));
  const z = useJanela(gravados.length >= 2 ? gravados : pontos, 0.12,
    { x: 'log', y: 'log' });
  const provisorios = linhas.flatMap((l) => METODOS_CV
    .filter((m) => l.por[m.chave].provisorio)
    .map((m) => l.por[m.chave].cv as number));
  const janela = useMemo(() => {
    if (!provisorios.length) return z.janela;
    const y0 = Math.min(z.janela.y0, ...provisorios.map((v) => v / 1.6));
    const y1 = Math.max(z.janela.y1, ...provisorios.map((v) => v * 1.6));
    return { ...z.janela, y0, y1 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [z.janela, provisorios.join('|')]);

  if (pontos.length < 2) {
    return (
      <section className="bloco">
        <h2><span>c<sub>v</sub> em conjunto</span></h2>
        <p className="nada">
          Com {pontos.length} traçado(s) não há conjunto para ler. O painel
          aparece a partir do segundo — é a comparação entre eles que ele
          existe para mostrar.
        </p>
      </section>
    );
  }

  return (
    <section className="bloco cv-conjunto">
      <h2>
        <span>c<sub>v</sub> em conjunto</span>
        <Dica rotulo="o painel do conjunto">
          Todos os estágios de carregamento, contra a tensão média do
          estágio (8.4), nos dois eixos em escala logarítmica. O estágio
          aberto aparece marcado e acompanha as alças enquanto você
          arrasta. O painel <strong>não julga</strong>: o c<sub>v</sub> cai
          com a tensão no trecho virgem e pode subir na recompressão — qual
          é qual é leitura de quem assina, e um sistema que pintasse de
          vermelho o ponto «errado» ensinaria a confiar na cor em vez da
          curva.
        </Dica>
      </h2>

      <ul className="legenda-cv">
        {METODOS_CV.map((m) => (
          <li key={m.chave} className={m.chave}>
            <Marcador metodo={m.chave} />
            {m.rotulo}
          </li>
        ))}
      </ul>

      <div className="grafico cv-serie">
        <Plano
          janela={janela}
          escalaX="log"
          escalaY="log"
          tituloX="Tensão média do estágio (kPa)"
          tituloY="cv (cm²/s)"
        >
          {({ px }) => (
            <>
              {METODOS_CV.map((m) => (
                <g key={m.chave} className={`serie-cv ${m.chave}`}>
                  <Serie pontos={serieDe(m.chave)} px={px} marcar={false} />
                </g>
              ))}
              {METODOS_CV.flatMap((m) => linhas
                .filter((l) => l.por[m.chave].cv != null)
                .map((l) => {
                  const q = px({ x: l.media, y: l.por[m.chave].cv as number });
                  const r = l.ehAberto ? 7 : 4.5;
                  return (
                    <g key={`${l.id}-${m.chave}`}
                      className={`cv-ponto ${m.chave}${l.ehAberto ? ' aberto' : ''}`}>
                      {m.chave === 'taylor'
                        ? <circle cx={q.x} cy={q.y} r={r} />
                        : (
                          <polygon points={`${q.x},${q.y - r} ${q.x + r},${q.y} `
                            + `${q.x},${q.y + r} ${q.x - r},${q.y}`} />
                        )}
                      {m.chave === 'taylor' && (
                        <text x={q.x} y={q.y - 11}>{l.ordem}</text>
                      )}
                      <title>{`Estágio ${l.ordem} · ${m.rotulo}`}</title>
                    </g>
                  );
                }))}
            </>
          )}
        </Plano>
      </div>

      <table className="dados">
        <thead>
          <tr>
            <th>Estágio</th>
            <th className="num">Tensão (kPa)</th>
            <th className="num">Tensão média</th>
            {METODOS_CV.map((m) => (
              <th className="num" key={m.chave}>
                c<sub>v</sub> {m.chave === 'taylor' ? 'Taylor' : 'Casagrande'}
              </th>
            ))}
            {/*
              * A razão com o estágio anterior — a conta que quem lê faz
              * de cabeça, feita aqui para ela não ter de ser feita nove
              * vezes, sobre o método de referência de cada estágio. Sem
              * juízo: 0,4 pode ser o esperado num trecho e um sinal de
              * traçado torto em outro.
              */}
            <th className="num">c<sub>v</sub> ÷ anterior</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => {
            const ant = linhas[i - 1];
            const meu = l.por[l.ref].cv;
            const dele = ant ? ant.por[ant.ref].cv : null;
            const razao = dele != null && dele > 0 && meu != null ? meu / dele : null;
            const provisorio = METODOS_CV.some((m) => l.por[m.chave].provisorio);
            return (
              <tr key={l.id} className={l.ehAberto ? 'ativa' : undefined}>
                <td>
                  {l.ordem}
                  {provisorio && (
                    <small className="det linha-de-baixo">
                      traçando — ainda não gravado
                    </small>
                  )}
                </td>
                <td className="num">{num(l.pressao, 1)}</td>
                <td className="num">{num(l.media, 1)}</td>
                {METODOS_CV.map((m) => (
                  <td className="num" key={m.chave}>
                    {l.por[m.chave].cv == null ? '—'
                      : cientifica(l.por[m.chave].cv as number)}
                    {l.ref === m.chave && l.por[m.chave].cv != null && (
                      <small className="det"> ref.</small>
                    )}
                  </td>
                ))}
                <td className="num">
                  {razao == null ? '—'
                    : razao.toLocaleString('pt-BR',
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

// ── 2 · Cr, Cc e Cs ──────────────────────────────────────────────────

function Indices({
  d, id, gravar, salvando, pode, aoSugerirSigma, sel, setSel,
}: BlocoProps & {
  pode: boolean;
  aoSugerirSigma(v: number | null): void;
  sel: Selecao;
  setSel: React.Dispatch<React.SetStateAction<Selecao>>;
}) {
  const [ativo, setAtivo] = useState<Trecho>('compressao');

  /*
   * O que já está gravado volta para a seleção.
   *
   * Sem isto, reabrir a tela mostrava a tabela com o Cc gravado e o
   * gráfico sem reta nenhuma — como se o trabalho tivesse sumido. A
   * `vw_adensamento_indice` já devolve os dois estágios de cada trecho;
   * era só usá-los.
   */
  useEffect(() => {
    if (!d.indices?.length) return;
    const inicial: Selecao = {};
    for (const i of d.indices as any[]) {
      inicial[i.trecho] = { a: i.estagio1_id, b: i.estagio2_id };
    }
    setSel((antes) => (Object.keys(antes).length ? antes : inicial));
  }, [d.indices]);

  /**
   * Um clique na curva preenche a ponta que ainda está livre.
   *
   * Primeiro clique escolhe «de», segundo escolhe «até», terceiro começa
   * de novo. É a mesma ordem de quem traça a régua no papel — e clicar
   * outra vez no mesmo ponto o solta, para corrigir sem precisar limpar
   * os dois.
   */
  const clicar = (estagioId: string) => {
    if (!pode) return;
    setSel((antes) => {
      const atual = antes[ativo] ?? {};
      if (atual.a === estagioId) return { ...antes, [ativo]: { ...atual, a: undefined } };
      if (atual.b === estagioId) return { ...antes, [ativo]: { ...atual, b: undefined } };
      if (!atual.a) return { ...antes, [ativo]: { ...atual, a: estagioId } };
      if (!atual.b) return { ...antes, [ativo]: { ...atual, b: estagioId } };
      return { ...antes, [ativo]: { a: estagioId, b: undefined } };
    });
  };

  const opcoes: Opcao[] = d.estagios.map((e) => ({
    valor: e.id, texto: `${e.ordem} · ${num(e.pressao_kpa, 1)} kPa`,
  }));

  /**
   * Gravar ao FECHAR o par, sem botão.
   *
   * «os índices e tensão de pré-adensamento deverão ser atualizadas na
   *  tabela automaticamente ao lançar os pontos» — Humberto, 29/08/2026.
   *
   * A tabela abaixo mostra o que o BANCO calculou (`vw_adensamento_indice`
   * faz Cc = |Δe / Δlog σ'|). Ela só podia se atualizar sozinha de duas
   * maneiras: repetindo a fórmula aqui em TypeScript, ou gravando assim
   * que o segundo ponto é clicado.
   *
   * A primeira está fora de questão. Cc em dois lugares é a `D-29` ao
   * contrário — e o dia em que as duas divergissem, quem descobriria
   * seria o cliente, no laudo. Então grava.
   *
   * O que impede o laço: a assinatura do que está na TELA é comparada
   * com a do que voltou do SERVIDOR. Iguais, não há o que gravar — e é
   * isso que faz a hidratação inicial (que preenche `sel` a partir de
   * `d.indices`) não disparar uma escrita do que já estava lá.
   */
  const assinatura = (pares: Array<{ t: string; a?: string; b?: string }>) =>
    pares.filter((p) => p.a && p.b)
      .map((p) => `${p.t}:${p.a}:${p.b}`).sort().join('|');

  const naTela = assinatura(
    TRECHOS.map((t) => ({ t: t.chave, a: sel[t.chave]?.a, b: sel[t.chave]?.b })));
  const noBanco = assinatura((d.indices as any[] ?? []).map((i) => ({
    t: i.trecho, a: i.estagio1_id, b: i.estagio2_id })));

  useEffect(() => {
    if (!pode || !naTela || naTela === noBanco || salvando) return;
    const indices = TRECHOS
      .filter((t) => sel[t.chave]?.a && sel[t.chave]?.b)
      .map((t) => ({
        trecho: t.chave,
        estagio1Id: sel[t.chave]!.a!,
        estagio2Id: sel[t.chave]!.b!,
      }));
    void gravar('indices', () => gravarIndices(id, { indices }));
  }, [naTela, noBanco, pode, salvando]);

  return (
    <section className="bloco">
      <h2>2 · Índices de compressão</h2>
      <p className="sub">
        Dois estágios definem cada reta. Quais são eles é leitura de quem
        olha a curva — o sistema calcula a inclinação, não escolhe os pontos.
      </p>

      <Escolha
        rotulo="Trecho que estou traçando" valor={ativo} limpavel={false}
        classe="rolar" desabilitado={!pode}
        aoEscolher={(v) => setAtivo((v as Trecho) ?? 'compressao')}
        opcoes={TRECHOS.map((t) => ({
          valor: t.chave,
          texto: `${t.nome}${sel[t.chave]?.a && sel[t.chave]?.b ? ' ✓' : ''}`,
        }))}
      />

      <CurvaNoGrafico
        estagios={d.estagios}
        selecao={sel}
        trechoAtivo={ativo}
        aoClicarPonto={clicar}
        e0={d.estagios[0]?.indice_vazios_inicial != null
          ? Number(d.estagios[0].indice_vazios_inicial) : null}
        preAdensamento={d.pre?.pressao_kpa != null ? Number(d.pre.pressao_kpa) : null}
        pode={pode}
        aoSugerirSigma={aoSugerirSigma}
        mostrarPacheco={false}
        marca="indices"
        faltas={d.faltas}
      />

      {/*
        * O par escolhido, em uma linha.
        *
        * Antes eram SEIS listas suspensas — «de» e «até» para cada um dos
        * três trechos —, mais a lista de trechos, mais a de quem traçou.
        * Oito campos para uma tarefa que é clicar em dois pontos da curva.
        *
        * O caminho passou a ser: escolher o trecho na fileira acima,
        * clicar nos dois pontos do gráfico, conferir aqui. As listas
        * continuam existindo para quem prefere teclado, escondidas atrás
        * de «escolher pela lista» — o que some é o entulho, não a opção.
        */}
      <div className="par-escolhido">
        {TRECHOS.map((t) => {
          const p = sel[t.chave] ?? {};
          const de = d.estagios.find((e: any) => e.id === p.a);
          const ate = d.estagios.find((e: any) => e.id === p.b);
          const completo = !!de && !!ate;
          return (
            <div
              key={t.chave}
              className={`par${t.chave === ativo ? ' ativo' : ''}`}
              onClick={() => pode && setAtivo(t.chave)}
            >
              <span className="par-nome">{t.nome}</span>
              <span className={completo ? 'par-valor' : 'par-valor vazio'}>
                {completo
                  ? `${num(de.pressao_kpa, 1)} → ${num(ate.pressao_kpa, 1)} kPa`
                  : de ? `${num(de.pressao_kpa, 1)} kPa → escolha o segundo ponto`
                  : 'clique em dois pontos da curva'}
              </span>
              {(p.a || p.b) && pode && (
                <button
                  type="button" className="so-icone" title="limpar este trecho"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setSel({ ...sel, [t.chave]: {} });
                  }}
                >×</button>
              )}
            </div>
          );
        })}
      </div>

      {/*
        * Os nomes das colunas são os da VISÃO, conferidos.
        *
        * Esta tabela lia `i.indice`, `i.de_ordem` e `i.ate_ordem`, e a
        * `vw_adensamento_indice` chama isso de `coeficiente`,
        * `estagio1_ordem` e `estagio2_ordem`. O resultado era o que
        * sempre é: três colunas em branco, sem erro nenhum no console —
        * `undefined` formatado vira travessão. Só apareceu quando a tela
        * foi fotografada com um Cc de verdade gravado.
        *
        * É exatamente a falha que o teste `16.2` existe para evitar do
        * lado do banco; do lado da tela, quem pega é olhar.
        */}
      {d.indices.length > 0 && (
        <div className="rola">
          <table className="dados">
            <thead>
              <tr><th>Trecho</th><th className="num">Índice</th>
                <th className="num">De (kPa)</th><th className="num">Até (kPa)</th>
                <th className="num">Estágios</th></tr>
            </thead>
            <tbody>
              {d.indices.map((i: any) => (
                <tr key={i.trecho}>
                  <td>{TRECHOS.find((t) => t.chave === i.trecho)?.nome
                       ?? rotulo(i.trecho)}</td>
                  <td className="num mono">{num(i.coeficiente, 4)}</td>
                  <td className="num mono">{num(i.pressao1_kpa, 1)}</td>
                  <td className="num mono">{num(i.pressao2_kpa, 1)}</td>
                  <td className="num">
                    {i.estagio1_ordem} → {i.estagio2_ordem}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <details className="alternativa">
        <summary>Escolher pela lista, sem clicar no gráfico</summary>
        <div className="form">
          {TRECHOS.map((t) => (
            <div key={t.chave} className="linha">
              <Escolha rotulo={`${t.nome} — de`} opcoes={opcoes}
                valor={sel[t.chave]?.a ?? null} classe="rolar" desabilitado={!pode}
                aoEscolher={(v) =>
                  setSel({ ...sel, [t.chave]: { ...sel[t.chave], a: v ?? undefined } })}
              />
              <Escolha rotulo="até" opcoes={opcoes} classe="rolar"
                valor={sel[t.chave]?.b ?? null} desabilitado={!pode}
                aoEscolher={(v) =>
                  setSel({ ...sel, [t.chave]: { ...sel[t.chave], b: v ?? undefined } })}
              />
            </div>
          ))}
        </div>
      </details>

      {/*
        * Não há botão «Gravar índices».
        *
        * O segundo clique fecha a reta e o registro segue junto. O que
        * sobra é dizer que seguiu — um traçado que some no silêncio é
        * pior que um botão a mais.
        */}
      <p className="nota">
        {salvando === 'indices'
          ? 'Gravando o traçado…'
          : naTela && naTela === noBanco
            ? 'Traçado gravado em seu nome. Clicar em outros dois pontos'
              + ' regrava o trecho.'
            : 'Os índices são gravados assim que o segundo ponto de cada'
              + ' reta é escolhido.'}
      </p>
    </section>
  );
}

// ── 3 · pré-adensamento ──────────────────────────────────────────────

function PreAdensamento({
  d, id, gravar, salvando, pode, campoErro, sugerida,
  selecao, aoSugerirSigma,
}: BlocoProps & {
  pode: boolean;
  campoErro(c: string): string | undefined;
  /** O que a construção de Pacheco Silva mostrou no gráfico DESTE bloco. */
  sugerida: number | null;
  /*
   * A mesma seleção do bloco 2, só para LER.
   *
   * A construção de Pacheco Silva precisa do trecho virgem, e o trecho
   * virgem é escolhido lá em cima. Duplicar a escolha aqui abriria a porta
   * para dois trechos virgens diferentes no mesmo ensaio — e para um laudo
   * cujo gráfico de índices discorda do de pré-adensamento.
   */
  selecao: Selecao;
  aoSugerirSigma(v: number | null): void;
}) {
  const [metodo, setMetodo] = useState<string>('pacheco_silva');
  const [f, setF] = useState<Numerico>({});

  useEffect(() => {
    if (!d.pre) return;
    setMetodo(d.pre.metodo);
    setF({ pressaoKpa: paraCampo(d.pre.pressao_kpa),
           indiceVazios: paraCampo(d.pre.indice_vazios) });
  }, [d.pre]);

  /**
   * A σ'p vem sozinha quando a reta virgem fecha.
   *
   * Sem `pressaoKpa` no corpo, o servidor refaz Pacheco Silva sobre o
   * trecho gravado (`fn_adensamento_sigma_p`, migração 0060) e grava
   * `origem = 'construcao'`.
   *
   * ─── Por que o gatilho é o trecho GRAVADO, e não o clicado ──────────
   *
   * Porque a construção precisa da reta virgem NO BANCO. Se este bloco
   * disparasse junto com o clique, ele correria com a gravação dos
   * índices do bloco 2 — e perderia a corrida na metade das vezes,
   * devolvendo «a construção precisa do trecho virgem» para quem acabou
   * de traçá-lo. Olhando para `d.indices`, ele só acorda depois que o
   * trecho existe do outro lado.
   *
   * ─── O que ele NÃO faz ──────────────────────────────────────────────
   *
   * Não atropela valor digitado. Se o engenheiro escreveu outra σ'p, ela
   * fica; quando o trecho virgem muda depois disso, a tela mostra as
   * duas e deixa a escolha com ele. Um sistema que apaga o número que
   * uma pessoa escreveu está discordando dela em silêncio.
   */
  const virgem = (d.indices as any[] ?? [])
    .find((i) => i.trecho === 'compressao');
  const chaveVirgem = virgem
    ? `${virgem.estagio1_id}:${virgem.estagio2_id}` : '';
  const ehPacheco = metodo.startsWith('pacheco_silva');

  useEffect(() => {
    if (!pode || !chaveVirgem || !ehPacheco || salvando) return;
    if (d.pre?.origem === 'digitada') return;
    if (d.pre?.pressao_kpa != null
        && Number(d.pre.pressao_kpa) === Number(d.pre.sigma_p_construcao)) return;
    void gravar('pre', () => gravarPreAdensamento(id, { metodo }));
  }, [chaveVirgem, ehPacheco, pode, salvando,
      d.pre?.pressao_kpa, d.pre?.sigma_p_construcao]);

  /* Com `pressaoKpa`, o registro guarda `origem = 'digitada'`. */
  const enviar = () => gravar('pre', () => gravarPreAdensamento(id, {
    metodo, pressaoKpa: n(f, 'pressaoKpa'),
    indiceVazios: n(f, 'indiceVazios'),
  }));

  return (
    <section className="bloco">
      <h2>3 · Tensão de pré-adensamento</h2>
      <p className="sub">
        A construção é traçada sobre a curva; o laudo redesenha a partir do
        valor gravado. Desenhar linhas que levem a outro σ'p seria o laudo
        discordar de si mesmo. Quando a reta virgem fecha no bloco 2, a
        σ'p de Pacheco Silva é gravada em seu nome — para trocá-la,
        escreva outro valor abaixo.
      </p>

      {/*
        * O gráfico DESTE bloco: a mesma curva, mas com a construção e sem
        * as retas de índice competindo por atenção. Só de leitura — o
        * trecho virgem se escolhe no bloco 2, e um clique aqui que mudasse
        * a escolha de lá seria a mesma decisão em dois lugares.
        */}
      <CurvaNoGrafico
        estagios={d.estagios}
        selecao={selecao}
        trechoAtivo={'compressao' as Trecho}
        aoClicarPonto={() => {}}
        e0={d.estagios[0]?.indice_vazios_inicial != null
          ? Number(d.estagios[0].indice_vazios_inicial) : null}
        preAdensamento={d.pre?.pressao_kpa != null
          ? Number(d.pre.pressao_kpa) : null}
        pode={false}
        aoSugerirSigma={aoSugerirSigma}
        mostrarPacheco
        marca="pre"
        faltas={d.faltas}
      />

      {!selecao.compressao?.a || !selecao.compressao?.b ? (
        <Aviso tipo="atencao">
          A construção de Pacheco Silva precisa do trecho de compressão
          (a reta virgem). Escolha os dois pontos dele no bloco 2 e ela
          aparece aqui sozinha.
        </Aviso>
      ) : null}

      {/*
        * O método sai da `linha`.
        *
        * `.linha > *` reparte a largura por igual, e três botões de nomes
        * longos espremidos num quinto da tela empilham verticalmente —
        * que foi o que a fotografia mostrou. Método numa linha, os
        * números na seguinte.
        */}
      <Escolha
        rotulo="Método" valor={metodo} limpavel={false} desabilitado={!pode}
        aoEscolher={(v) => setMetodo(v ?? 'pacheco_silva')}
        opcoes={[
          { valor: 'pacheco_silva', texto: 'Pacheco Silva' },
          { valor: 'casagrande', texto: 'Casagrande' },
          { valor: 'pacheco_silva_log1e', texto: 'P. Silva — log(1+e)' }]}
      />
      {/*
        * A σ'p da construção GRAVA SOZINHA quando a reta virgem fecha —
        * ver o efeito lá em cima. Este comentário dizia o contrário
        * («a sugestão é oferecida, nunca imposta») e a auditoria de
        * 03/09/2026 (UX-040) leu a tela pelo comentário. O que vale é o
        * que a nota abaixo diz à pessoa: gravada da construção, em seu
        * nome, e substituível à mão. A norma manda declarar método e
        * autor — os dois ficam registrados (`origem`, `responsavel_id`).
        */}
      {/*
        * De onde veio o número que está gravado.
        *
        * «Construção» e «digitada» pesam diferente numa revisão, e o
        * registro passou a saber distinguir os dois (coluna `origem`,
        * migração 0060). Dizer isso na tela é o que impede alguém de
        * assumir que a σ'p do laudo foi conferida por gente quando não
        * foi — e vice-versa.
        */}
      {d.pre?.origem === 'construcao' && (
        <p className="nota">
          σ′p <strong>gravada da construção</strong> de Pacheco Silva sobre
          o trecho virgem, em seu nome. Escrever outro valor abaixo
          substitui — e o registro passa a dizer que foi à mão.
        </p>
      )}

      {/*
        * As duas divergirem não é erro: é o trecho virgem ter sido
        * retraçado depois que alguém escreveu a σ'p à mão. Quem decide
        * qual vale é o engenheiro; o sistema mostra as duas.
        */}
      {d.pre?.ajustada_a_mao && d.pre?.sigma_p_construcao != null
        && Number(d.pre.sigma_p_construcao) !== Number(d.pre.pressao_kpa) && (
        <Aviso tipo="atencao" titulo="O valor gravado foi digitado">
          A construção sobre o trecho virgem de hoje dá{' '}
          <strong>{num(d.pre.sigma_p_construcao, 1)} kPa</strong>; está
          gravado <strong>{num(d.pre.pressao_kpa, 1)} kPa</strong>, escrito
          à mão. Se o traçado mudou depois, apague o campo e grave para
          voltar à construção.
        </Aviso>
      )}

      {sugerida != null && metodo === 'casagrande' && pode && (
        <p className="nota">
          A construção de Pacheco Silva no gráfico acima daria{' '}
          <strong>{num(sugerida, 1)} kPa</strong> — pelo método de
          Casagrande, trace e escreva o valor encontrado.
        </p>
      )}

      <div className="form linha">
        <Campo rotulo="σ'p (kPa)" inputMode="decimal" value={f.pressaoKpa ?? ''}
          disabled={!pode} erro={campoErro('pressaoKpa')}
          onChange={(e) => setF({ ...f, pressaoKpa: e.target.value })} />
        {/*
          * Este campo não é enfeite: sem ele o LAUDO não consegue
          * redesenhar a construção de Pacheco Silva, porque a construção
          * precisa de dois números — onde σ'p cai no eixo horizontal e
          * onde ela cai na curva.
          *
          * Quando há trecho virgem traçado, o servidor preenche sozinho
          * (`fn_adensamento_e_em_sigma_p`). O caso que sobra é o da σ'p
          * escrita à mão sem trecho virgem — e era exatamente esse que
          * fazia a folha 4 sair com a curva limpa, sem aviso nenhum, até
          * 30/08/2026.
          */}
        <Campo rotulo="e em σ'p" inputMode="decimal" value={f.indiceVazios ?? ''}
          disabled={!pode}
          dica={f.indiceVazios
            ? 'O laudo usa este valor para redesenhar a construção.'
            : 'Sai sozinho da construção sobre o trecho virgem. Se você '
              + 'escreveu a σ\'p à mão sem traçar o trecho, preencha aqui — '
              + 'sem ele o laudo imprime o número mas não o traçado.'}
          onChange={(e) => setF({ ...f, indiceVazios: e.target.value })}
        />
        <button type="button" disabled={!pode || salvando === 'pre'}
          onClick={() => void enviar()}>
          {salvando === 'pre' ? 'Gravando…' : 'Gravar à mão'}
        </button>
      </div>
    </section>
  );
}
